import Stepper from "pages/SelfServe/sharedComponents/Stepper";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Email, CheckCircle } from "@mui/icons-material";
import { auth } from "firebaseClient";
import { sendEmailVerification } from "firebase/auth";
import themes from "config/themes";

interface EmailVerifyProps {
  setShowEmailVerification: (show: boolean) => void;
  checkEmailVerification: () => Promise<void>;
  isCheckingVerification: boolean;
  err: string | null;
}

export const EmailVerify: React.FC<EmailVerifyProps> = ({
  setShowEmailVerification,
  checkEmailVerification,
  isCheckingVerification,
  err,
}) => {
  const { currentUser } = auth!;
  const resend = async () => {
    if (!currentUser) return;
    try {
      await sendEmailVerification(currentUser);
    } catch (err: unknown) {
      console.error("Error sending verification email:", err);
    }
  };
  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Stepper currentStep={3} />

      <Box
        sx={{
          flex: 1,
          padding: { xs: 3, md: "16px 48px" },
          backgroundColor: themes.palette.neutral.main,
          display: "flex",
        }}
      >
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}
        <Paper
          elevation={3}
          sx={{
            p: 6,
            maxWidth: 500,
            textAlign: "center",
            borderRadius: 2,
          }}
        >
          <Email
            sx={{
              fontSize: 64,
              color: "primary.secondary",
              mb: 3,
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            Verify Your Email
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            We've sent a verification email to{" "}
            <strong>{currentUser?.email}</strong>. Please check your inbox and
            click the verification link to continue.
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
            Don't see the email? Check your spam folder. After clicking the
            verification link in your email, return here and click the button
            below to continue.
          </Alert>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={checkEmailVerification}
              disabled={isCheckingVerification}
              startIcon={
                isCheckingVerification ? (
                  <CircularProgress size={20} />
                ) : (
                  <CheckCircle />
                )
              }
              sx={{
                bgcolor: "success.main",
                color: "white",
                py: 1.5,
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "success.dark",
                },
              }}
            >
              {isCheckingVerification
                ? "Checking..."
                : "I've Verified My Email"}
            </Button>

            <Button
              variant="text"
              onClick={() => setShowEmailVerification(false)}
            >
              Go Back
            </Button>
            <Button variant="text" onClick={resend}>
              Resend
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};
